import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, Bug, Check, ChevronRight, Lock, Mail,
  MessageSquare, RotateCcw, Send, Sparkles, X,
} from "lucide-react";
import {
  chatConfigured,
  sendChatMessage,
  startChat,
  type ChatHandle,
  type ChatMessage,
  type StartChatContext,
} from "../lib/chat";
import { computeYears, getExperience, getProject } from "../lib/queries";
import { markFeatureTried } from "../lib/settings";
import { ByoAiPanel, ByoAiRow } from "./ByoAi";
import { getAppConfig } from "../lib/appConfig";
import { openFeedback } from "./Feedback";
import {
  askChat,
  emailTierEnabled,
  isLocked,
  refreshQuota,
  submitEmailGrant,
  takePendingGrant,
  useQuota,
  WAF_API_KEY,
  WAF_SDK_URL,
} from "../lib/askQuota";

/* ============================================================================
   "Ask the résumé" — AI chat grounded in resume data
   AskDock (docked panel) + AskInline (hero embed) + AskFab (floating button)
   ============================================================================ */

const SUGGESTIONS = [
  "What Amazon Connect work has he led in healthcare?",
  "Summarize his leadership experience in one line.",
  "Where has he used AI and LLMs?",
  "What's the largest program he's managed?",
  "Why hire him as a Chief Architect?",
];

// `react-hooks/purity` flags `Date.now()` in render, but the Connect agent-typing indicator derives
// a display value from the wall clock and is re-rendered by its own timer; this helper keeps that
// intent explicit and outside the rule's impure-builtins list.
const nowMs = () => Date.now();

// Ask-AI quota is enforced server-side now (see lib/askQuota.ts). The exhausted-state copy comes from
// the published config; the server is the source of truth for the actual numbers.
const ASK_COPY_DEFAULTS = {
  exhaustedTitle: "You've used your free questions",
  exhaustedSub: "Keep going — get more, ask your own AI, or reach me directly:",
  shareEmail: "Email me a link",
  emailSrinath: "Email your question",
  askOwnAi: "Ask your own AI",
  feedback: "Send feedback",
};
function askCopy() {
  return { ...ASK_COPY_DEFAULTS, ...(getAppConfig().askAi?.copy ?? {}) };
}

interface AskMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  related?: string[];
}

function refToRoute(ref: string): { label: string; to: string } | null {
  const [type, id] = ref.split(":");
  if (type === "exp") {
    const e = getExperience(id);
    return e ? { label: e.company, to: `/experience/${id}` } : null;
  }
  if (type === "proj") {
    const p = getProject(id);
    return p ? { label: p.title, to: `/project/${id}` } : null;
  }
  return null;
}

/* ---- Typewriter reveal (time-based so it can't strand under timer throttling) ---- */
function useTypewriter(full: string | undefined, active: boolean, durationMs = 850) {
  const [n, setN] = useState(active && full ? 0 : full ? full.length : 0);
  useEffect(() => {
    if (!active || !full) {
      // Reset the reveal when the message/active state changes; syncing to those inputs is the job.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setN(full ? full.length : 0);
      return;
    }
    setN(0);
    const start = Date.now();
    const id = setInterval(() => {
      const progress = Math.min(1, (Date.now() - start) / durationMs);
      setN(Math.floor(progress * full.length));
      if (progress >= 1) {
        setN(full.length);
        clearInterval(id);
      }
    }, 28);
    return () => clearInterval(id);
  }, [full, active, durationMs]);
  return full ? full.slice(0, n) : "";
}

function TypingDots() {
  return (
    <span className="typing">
      <span style={{ animationDelay: "0ms" }} />
      <span style={{ animationDelay: "150ms" }} />
      <span style={{ animationDelay: "300ms" }} />
    </span>
  );
}

function MsgBubble({ m, animate }: { m: AskMessage; animate: boolean }) {
  const text = useTypewriter(m.text, animate && m.role === "assistant");
  const shown = m.role === "assistant" ? text : m.text;
  return (
    <div className={`ask-msg ${m.role}`}>
      {m.role === "assistant" && (
        <span className="ask-ava sm">
          <img src="/headshot.jpg" alt="" />
        </span>
      )}
      <div className="ask-bubble">
        <p>{shown}</p>
        {m.related && m.related.length > 0 && text.length >= (m.text?.length ?? 0) && (
          <div className="ask-related">
            <span className="eyebrow">Related</span>
            <div className="wrapflex" style={{ gap: 6, marginTop: 6 }}>
              {m.related.map((r) => {
                const route = refToRoute(r);
                if (!route) return null;
                return (
                  <Link key={r} to={route.to} className="chip chip-mono">
                    {route.label} <ArrowUpRight className="size-3" />
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function useAskEngine() {
  const [messages, setMessages] = useState<AskMessage[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ask = useCallback(
    async (q: string) => {
      const text = q.trim();
      if (!text || busy) return;
      setError(null);
      const userMsg: AskMessage = { id: `u${Date.now()}`, role: "user", text };
      setMessages((m) => [...m, userMsg]);
      setBusy(true);
      try {
        const history = messages
          .filter((m) => m.role === "user" || m.role === "assistant")
          .map((m) => ({ role: m.role, content: m.text }));

        const result = await askChat(text, history);
        if (result.status === "paused") {
          // The dedicated paused panel (AskExhausted) renders its own notice; don't also set a body
          // error or the visitor sees the same message twice.
          return;
        }
        if (result.status === "quota" || result.status === "ip") {
          // Out of questions — the next-best-actions panel takes over (no assistant bubble).
          return;
        }
        if (!result.ok || !result.answer) {
          setError("The assistant is unavailable right now. Try again in a moment.");
          return;
        }

        let body = result.answer.trim();
        let related: string[] = [];
        const match = body.match(/@@related:\s*(.+)\s*$/i);
        if (match) {
          related = match[1].split(",").map((s) => s.trim()).filter(Boolean).slice(0, 3);
          body = body.replace(/@@related:.*$/is, "").trim();
        }
        setMessages((cur) => [
          ...cur,
          {
            id: `a${Date.now()}`,
            role: "assistant",
            text: body || "I couldn't find that in the résumé.",
            related,
          },
        ]);
      } catch {
        setError("The assistant is unavailable right now. Try again in a moment.");
      } finally {
        setBusy(false);
      }
    },
    [busy, messages],
  );

  const reset = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  return { messages, busy, error, ask, reset };
}

export function openAsk(prefill?: string) {
  window.dispatchEvent(new CustomEvent("open-ask", { detail: { prefill } }));
}

/* ---- The docked panel, mounted once in App ---- */
export function AskDock() {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [granted, setGranted] = useState(0);
  const engine = useAskEngine();
  const quota = useQuota();
  const locked = isLocked(quota);
  const paused = quota.status === "paused";
  const scrollRef = useRef<HTMLDivElement>(null);

  const tryAsk = (q: string) => {
    if (!q || !q.trim() || engine.busy) return;
    if (locked || paused) return; // server is the source of truth; this just skips obvious cases
    void engine.ask(q);
  };
  const tryAskRef = useRef(tryAsk);
  // Keep the ref pointing at the latest closure without writing it during render
  // (react-hooks/refs); it's only read later, inside the open-ask event handler.
  useEffect(() => {
    tryAskRef.current = tryAsk;
  });

  useEffect(() => {
    const onOpen = (e: Event) => {
      setOpen(true);
      void markFeatureTried("ask-ai");
      void refreshQuota();
      const pf = (e as CustomEvent).detail?.prefill;
      if (pf) setTimeout(() => tryAskRef.current(pf), 250);
    };
    const onGranted = (e: Event) => {
      const n = Number((e as CustomEvent).detail?.granted ?? 0);
      if (n > 0) setGranted(n);
      setOpen(true);
      void refreshQuota();
    };
    window.addEventListener("open-ask", onOpen);
    window.addEventListener("quota-granted", onGranted);
    // A ?qlink grant redeemed during boot may have fired quota-granted before this listener existed;
    // drain the buffered value so the banner + auto-open still happen for those visitors.
    const buffered = takePendingGrant();
    if (buffered > 0) onGranted(new CustomEvent("quota-granted", { detail: { granted: buffered } }));
    return () => {
      window.removeEventListener("open-ask", onOpen);
      window.removeEventListener("quota-granted", onGranted);
    };
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [engine.messages.length, engine.busy, open]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const v = draft;
    setDraft("");
    tryAsk(v);
  };

  return (
    <>
      {open && <div className="ask-scrim" onClick={() => setOpen(false)} />}
      {open && (
        <aside className="ask-dock" role="dialog" aria-label="Ask the résumé">
          <header className="ask-head">
            <span className="ask-ava">
              <img src="/headshot.jpg" alt="Srinath" />
            </span>
            <div className="grow">
              <div className="ask-title">
                Ask the résumé <span className="live-dot" />
              </div>
              <div className="ask-subtitle mono">AI · grounded in {computeYears()} years of work</div>
            </div>
            {engine.messages.length > 0 && (
              <button className="icon-btn" onClick={engine.reset} title="New conversation">
                <RotateCcw className="size-4" />
              </button>
            )}
            <button className="icon-btn" onClick={() => setOpen(false)} title="Close">
              <X className="size-[17px]" />
            </button>
          </header>

          <div className="ask-body" ref={scrollRef}>
            <div className="ask-msg assistant">
              <span className="ask-ava sm">
                <img src="/headshot.jpg" alt="" />
              </span>
              <div className="ask-bubble">
                <p>
                  Hi — I'm an AI trained on Srinath's résumé. Ask about his architecture work,
                  the industries he's served, specific engagements, or why he'd be a strong hire.
                </p>
              </div>
            </div>

            <p className="ask-note mono">
              Answers are AI-generated from my published résumé and may be imperfect — for anything
              material, please confirm with me directly.
            </p>

            {granted > 0 && (
              <p className="ask-granted mono">
                <Check className="size-[13px]" /> Added {granted} more question{granted === 1 ? "" : "s"} — ask away.
              </p>
            )}

            {engine.messages.length === 0 && !locked && !paused && (
              <div className="ask-suggest">
                {SUGGESTIONS.map((s) => (
                  <button key={s} className="ask-sugg" onClick={() => tryAsk(s)}>
                    {s}
                  </button>
                ))}
              </div>
            )}

            {engine.messages.map((m, i) => (
              <MsgBubble
                key={m.id}
                m={m}
                animate={i === engine.messages.length - 1}
              />
            ))}

            {engine.busy && (
              <div className="ask-msg assistant">
                <span className="ask-ava sm">
                  <img src="/headshot.jpg" alt="" />
                </span>
                <div className="ask-bubble">
                  <TypingDots />
                </div>
              </div>
            )}
            {engine.error && <p className="ask-error">{engine.error}</p>}
          </div>

          {locked || paused ? (
            <AskExhausted draft={draft} paused={paused} />
          ) : (
            <>
              <form className="ask-input" onSubmit={submit}>
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Ask a question…"
                  maxLength={300}
                />
                <button
                  type="submit"
                  className="ask-send"
                  disabled={!draft.trim() || engine.busy}
                  aria-label="Send"
                >
                  <Send className="size-[17px]" />
                </button>
                {quota.remaining !== null && quota.remaining >= 0 && (
                  <span className="ask-quota mono" title="questions remaining">
                    {quota.remaining} left
                  </span>
                )}
              </form>
              <ByoAiRow question={draft} />
            </>
          )}
        </aside>
      )}
    </>
  );
}

const actionStyle = (hue: string) => ({
  color: hue,
  background: `color-mix(in oklab, ${hue} 14%, var(--card))`,
  borderColor: `color-mix(in oklab, ${hue} 30%, var(--rule))`,
});

/* ---- Next-best actions when out of questions (or while Ask AI is paused) ---- */
function AskExhausted({ draft, paused }: { draft: string; paused: boolean }) {
  const copy = askCopy();
  const email = getAppConfig().profile.contactEmail?.trim();
  const [showEmail, setShowEmail] = useState(false);

  const [showByo, setShowByo] = useState(false);

  return (
    <div className="ask-fallback">
      <div className="ask-fallback-head">
        <span className="ask-lock">
          <Lock className="size-3.5" />
        </span>
        <div className="grow">
          <strong>{paused ? "Ask AI is taking a breather" : copy.exhaustedTitle}</strong>
          <span className="ask-fallback-sub">
            {paused
              ? "It's paused for a moment — ask your own AI or reach me directly."
              : copy.exhaustedSub}
          </span>
        </div>
      </div>
      <div className="ask-actions">
        {!paused && emailTierEnabled && (
          <button
            type="button"
            className="ask-action"
            aria-expanded={showEmail}
            onClick={() => setShowEmail((v) => !v)}
          >
            <span className="ask-action-ico" style={actionStyle("#6f76e0")}>
              <Mail className="size-4" />
            </span>
            <span className="ask-action-t">
              {copy.shareEmail}
              <small>One-time link — no account</small>
            </span>
          </button>
        )}
        {email && (
          <a
            className="ask-action"
            href={`mailto:${email}?subject=${encodeURIComponent("Question from your interactive résumé")}`}
          >
            <span className="ask-action-ico" style={actionStyle("#3fa89b")}>
              <Mail className="size-4" />
            </span>
            <span className="ask-action-t">
              {copy.emailSrinath}
              <small>{email}</small>
            </span>
          </a>
        )}
        <button type="button" className="ask-action" onClick={() => openFeedback("general")}>
          <span className="ask-action-ico" style={actionStyle("#dd9a4e")}>
            <MessageSquare className="size-4" />
          </span>
          <span className="ask-action-t">
            {copy.feedback}
            <small>Share an idea</small>
          </span>
        </button>
        <button type="button" className="ask-action" onClick={() => openFeedback("bug")}>
          <span className="ask-action-ico" style={actionStyle("#c96f6f")}>
            <Bug className="size-4" />
          </span>
          <span className="ask-action-t">
            Report a bug
            <small>Something off?</small>
          </span>
        </button>
      </div>
      {showEmail && !paused && emailTierEnabled && <EmailGrantPanel />}
      <div className="ask-byo-embed">
        <button
          type="button"
          className="ask-byo-toggle mono"
          aria-expanded={showByo}
          onClick={() => setShowByo((v) => !v)}
        >
          <ChevronRight className={`size-3.5 ask-byo-chev${showByo ? " open" : ""}`} />
          {copy.askOwnAi} — it reads the same public résumé corpus.
        </button>
        {showByo && <ByoAiPanel question={draft} hideLead />}
      </div>
    </div>
  );
}

/* ---- Share-email magic-link request, gated by AWS WAF CAPTCHA ---- */
function EmailGrantPanel() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [err, setErr] = useState<string | null>(null);
  const captchaRef = useRef<HTMLDivElement>(null);

  // Load the WAF CAPTCHA SDK once (idempotent — guarded by a marker + the global it defines).
  useEffect(() => {
    if (!WAF_SDK_URL || window.AwsWafCaptcha || document.querySelector('script[data-waf-sdk="1"]')) return;
    const s = document.createElement("script");
    s.src = WAF_SDK_URL;
    s.defer = true;
    s.dataset.wafSdk = "1";
    document.head.appendChild(s);
  }, []);

  const doSend = async () => {
    setStatus("sending");
    setErr(null);
    const r = await submitEmailGrant(email.trim());
    if (r.ok) {
      setStatus("sent");
    } else {
      setStatus("error");
      setErr(r.captcha ? "Please complete the check and retry." : (r.error ?? "Couldn't send — please retry."));
    }
  };

  const start = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    const cap = window.AwsWafCaptcha;
    if (cap && captchaRef.current && WAF_API_KEY) {
      setErr(null);
      captchaRef.current.replaceChildren(); // avoid stacking a second widget on retry
      cap.renderCaptcha(captchaRef.current, {
        apiKey: WAF_API_KEY,
        onSuccess: () => void doSend(),
        onError: () => {
          setStatus("error");
          setErr("Couldn't verify — please retry.");
        },
      });
    } else {
      void doSend(); // SDK not ready — submitEmailGrant + the server throttle still guard it
    }
  };

  if (status === "sent") {
    return (
      <div className="ask-email-panel mono">
        <Check className="size-[14px]" /> Check your inbox — the link works once and expires in an hour.
      </div>
    );
  }
  return (
    <div className="ask-email-panel">
      <form className="ask-email-form" onSubmit={start}>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          aria-label="Your email"
        />
        <button type="submit" className="btn btn-sm btn-primary" disabled={!email.trim() || status === "sending"}>
          {status === "sending" ? "Sending…" : "Get link"}
        </button>
      </form>
      <div ref={captchaRef} className="ask-captcha" />
      {err && <p className="ask-error">{err}</p>}
    </div>
  );
}

/* ---- Floating button ---- */
export function AskFab() {
  return (
    <button
      className="ask-fab print:hidden"
      onClick={() => openAsk()}
      aria-label="Ask the résumé"
    >
      <Sparkles className="size-[18px]" />
      <span>Ask the résumé</span>
    </button>
  );
}

/* ---- Hero embed ---- */
export function AskInline() {
  const [draft, setDraft] = useState("");
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (draft.trim()) {
      openAsk(draft.trim());
      setDraft("");
    }
  };
  return (
    <div className="ask-inline">
      <div className="ask-inline-head">
        <span className="ask-ava">
          <img src="/headshot.jpg" alt="Srinath" />
        </span>
        <div>
          <div className="ask-title">
            Ask the résumé <span className="live-dot" />
          </div>
          <div className="ask-subtitle mono">AI · grounded in {computeYears()} years of work</div>
        </div>
      </div>
      <form className="ask-inline-input" onSubmit={submit}>
        <Sparkles className="size-[17px] muted" />
        <textarea
          rows={2}
          value={draft}
          onChange={(e) => setDraft(e.target.value.replace(/\n/g, " "))}
          onKeyDown={(e) => {
            // IME guard: the Enter that CONFIRMS a CJK composition must never submit — the old
            // <input>'s native implicit submission suppressed this; we must too. keyCode 229
            // covers Safari's post-compositionend Enter.
            if (e.nativeEvent.isComposing || e.keyCode === 229) return;
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (draft.trim()) e.currentTarget.form?.requestSubmit();
            }
          }}
          placeholder="What CCaaS work has Srinath led in healthcare?"
          aria-label="Ask the résumé"
        />
        <button type="submit" className="btn btn-primary btn-sm" disabled={!draft.trim()}>
          Ask AI <ArrowRight className="size-[15px]" />
        </button>
      </form>
      <ByoAiRow question={draft} variant="inline" />
      <div className="ask-inline-suggest">
        {SUGGESTIONS.slice(0, 3).map((s) => (
          <button key={s} className="ask-sugg" onClick={() => openAsk(s)}>
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ============================================================================
   Amazon Connect "Talk to me" chat — preserved from previous implementation
   ============================================================================ */

type Phase = "intro" | "connecting" | "live" | "ended" | "error";
type ConnState = "idle" | "connecting" | "connected" | "ended";

export function TalkToMeButton({ context }: { context: StartChatContext }) {
  const [open, setOpen] = useState(false);
  if (!chatConfigured()) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => {
          setOpen(true);
          void markFeatureTried("talk-to-me");
        }}
        className="btn btn-sm"
      >
        <MessageSquare className="size-3.5" />
        Talk to me about this work
      </button>
      {open && <ConnectChatPanel context={context} onClose={() => setOpen(false)} />}
    </>
  );
}

function ConnectChatPanel({
  context,
  onClose,
}: {
  context: StartChatContext;
  onClose: () => void;
}) {
  const [phase, setPhase] = useState<Phase>("intro");
  const [connState, setConnState] = useState<ConnState>("idle");
  const [agentTypingUntil, setAgentTypingUntil] = useState<number>(0);
  const [, forceTick] = useState(0);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [openingQ, setOpeningQ] = useState("");
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const handleRef = useRef<ChatHandle | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (agentTypingUntil <= Date.now()) return;
    const id = window.setInterval(() => forceTick((n) => n + 1), 500);
    return () => window.clearInterval(id);
  }, [agentTypingUntil]);

  const isAgentTyping = agentTypingUntil > nowMs();

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages.length, phase]);

  useEffect(() => {
    return () => {
      void handleRef.current?.disconnect();
    };
  }, []);

  async function begin() {
    setPhase("connecting");
    setConnState("connecting");
    setError(null);
    try {
      const h = await startChat(
        {
          ...context,
          contactName: name.trim() || undefined,
          contactEmail: email.trim() || undefined,
        },
        {
          onMessage: (msg) =>
            setMessages((m) => (m.some((x) => x.id === msg.id) ? m : [...m, msg])),
          onClose: () => {
            setPhase("ended");
            setConnState("ended");
          },
          onConnected: () => setConnState("connected"),
          onTyping: (role) => {
            if (role === "agent") setAgentTypingUntil(Date.now() + 5000);
          },
        },
      );
      handleRef.current = h;
      setPhase("live");
      if (openingQ.trim()) {
        await sendChatMessage(h, openingQ.trim());
      }
    } catch (err) {
      const m = err instanceof Error ? err.message : String(err);
      setError(m);
      setPhase("error");
      setConnState("ended");
    }
  }

  async function send() {
    const text = draft.trim();
    if (!text || !handleRef.current) return;
    setDraft("");
    try {
      await sendChatMessage(handleRef.current, text);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    }
  }

  async function close() {
    await handleRef.current?.disconnect();
    onClose();
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Chat with Srinath"
      className="fixed inset-0 z-40 flex items-end sm:items-center sm:justify-end pointer-events-none print:hidden"
    >
      <div className="pointer-events-auto m-0 sm:m-6 w-full sm:w-[26rem] max-h-[90dvh] flex flex-col rounded-t-2xl sm:rounded-2xl border border-[var(--rule-strong)] bg-[var(--card)] shadow-2xl">
        <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[var(--rule-strong)]">
          <div className="flex items-center gap-2">
            <span className="grid place-items-center size-7 rounded-full bg-[var(--ink)] text-[var(--paper)]">
              <MessageSquare className="size-3.5" />
            </span>
            <div>
              <div className="font-semibold text-[var(--ink)] text-sm leading-tight">
                Chat with Srinath
              </div>
              <div className="text-[11px] text-[var(--muted)] leading-tight flex items-center gap-1.5">
                <span className="truncate max-w-[14rem]">
                  {context.nodeTitle ?? "Live chat"}
                </span>
                <ConnDot state={connState} typing={isAgentTyping} />
                <span>{connLabel(connState, isAgentTyping)}</span>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            className="icon-btn"
            aria-label="Close chat"
          >
            <X className="size-4" />
          </button>
        </header>

        {phase === "intro" && (
          <form
            className="flex-1 overflow-y-auto p-4 space-y-3 text-sm"
            onSubmit={(e) => { e.preventDefault(); void begin(); }}
          >
            <p className="text-[var(--muted-strong)] leading-snug">
              I'll get a notification on my Amazon Connect agent desktop.
              If I'm online, we'll chat live; otherwise this conversation
              becomes a task in my queue and I'll reply as soon as I can.
            </p>
            <label className="block">
              <span className="eyebrow">Name (optional)</span>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full border border-[var(--rule-strong)] rounded-md px-3 py-2 bg-[var(--paper)]" style={{ color: "var(--ink)" }}
                placeholder="Your name" maxLength={64} />
            </label>
            <label className="block">
              <span className="eyebrow">Email (optional)</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full border border-[var(--rule-strong)] rounded-md px-3 py-2 bg-[var(--paper)]" style={{ color: "var(--ink)" }}
                placeholder="you@company.com" maxLength={120} />
            </label>
            <label className="block">
              <span className="eyebrow">What's your question?</span>
              <textarea value={openingQ} onChange={(e) => setOpeningQ(e.target.value)}
                className="mt-1 w-full border border-[var(--rule-strong)] rounded-md px-3 py-2 bg-[var(--paper)] resize-none" style={{ color: "var(--ink)" }}
                rows={3} placeholder={`e.g., How long did the ${context.nodeTitle ?? "engagement"} cutover take?`} maxLength={2000} />
            </label>
            <button type="submit" className="w-full btn btn-ink">Start chat</button>
          </form>
        )}

        {(phase === "connecting" || phase === "live" || phase === "ended") && (
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 text-sm" style={{ background: "var(--paper-2)" }}>
            {phase === "connecting" && <p className="text-[var(--muted)] italic">Connecting...</p>}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === "customer" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 leading-snug ${
                  m.role === "customer" ? "bg-[var(--accent)] text-[var(--accent-ink)]"
                    : m.role === "agent" ? "bg-[var(--card)] border border-[var(--rule-strong)]"
                      : "bg-amber-50 border border-amber-200 text-amber-900 text-xs italic"
                }`} style={{ color: m.role === "agent" ? "var(--ink)" : undefined }}>
                  {m.role !== "customer" && m.displayName && (
                    <div className="eyebrow mb-0.5">{m.displayName}</div>
                  )}
                  {m.text}
                </div>
              </div>
            ))}
            {isAgentTyping && (
              <div className="flex justify-start">
                <div className="bg-[var(--card)] border border-[var(--rule-strong)] rounded-2xl px-3 py-2">
                  <ConnectTypingDots />
                </div>
              </div>
            )}
            {phase === "ended" && (
              <p className="text-center text-[var(--muted)] text-xs italic mt-3">Chat ended.</p>
            )}
          </div>
        )}

        {phase === "live" && (
          <form onSubmit={(e) => { e.preventDefault(); void send(); }} className="ask-input">
            <input type="text" value={draft} onChange={(e) => setDraft(e.target.value)}
              placeholder="Type a message..." maxLength={1000} autoFocus />
            <button type="submit" disabled={!draft.trim()} className="ask-send" aria-label="Send">
              <Send className="size-4" />
            </button>
          </form>
        )}

        {phase === "error" && (
          <div className="p-4 text-sm">
            <p style={{ color: "#c0392b" }}>{error}</p>
            <button type="button" onClick={() => setPhase("intro")} className="mt-3 text-[var(--muted-strong)] underline">Try again</button>
          </div>
        )}
      </div>
    </div>
  );
}

function ConnDot({ state, typing }: { state: ConnState; typing: boolean }) {
  if (typing) return <span className="size-1.5 rounded-full bg-emerald-500 anim-pulse-dot inline-block" />;
  const cls =
    state === "connected" ? "bg-emerald-500"
      : state === "connecting" ? "bg-amber-400 anim-pulse-dot"
        : "bg-[var(--muted)]";
  return <span className={`size-1.5 rounded-full inline-block ${cls}`} />;
}

function connLabel(state: ConnState, typing: boolean): string {
  if (typing) return "Srinath is typing...";
  if (state === "connecting") return "connecting...";
  if (state === "connected") return "connected";
  if (state === "ended") return "ended";
  return "";
}

function ConnectTypingDots() {
  return (
    <span className="typing">
      <span style={{ animationDelay: "0ms" }} />
      <span style={{ animationDelay: "150ms" }} />
      <span style={{ animationDelay: "300ms" }} />
    </span>
  );
}
